import { useState, useEffect, useCallback } from 'react';
import { chybaZOdpovedi } from '../apiChyba';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const jsonHeaders = () => ({
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-TOKEN': csrf(),
});

export function useShiftData({ forceNext = false } = {}) {
    const [smena, setSmena] = useState(null);
    const [dochazka, setDochazka] = useState(null);
    const [pokladnaSmena, setPokladnaSmena] = useState(null);
    const [tym, setTym] = useState([]);
    const [typUvazku, setTypUvazku] = useState(null);
    const [jeDnes, setJeDnes] = useState(true);
    const [ukoly, setUkoly] = useState([]);
    const [loading, setLoading] = useState(true);

    /**
     * `tise = true` → data se obnoví bez loaderu.
     *
     * Modul nad tímhle hookem na `loading` vrací celou obrazovku „Načítám…",
     * takže každé obnovení dat rozebralo cockpit a složilo ho znovu. U manažera
     * to bylo vidět co 45 sekund (pojistka pro nefunkční WebSocket) a nebylo to
     * jen probliknutí: s remountem se resetoval i rozepsaný formulář uzávěrky —
     * opsaný terminál, napočítaná hotovost i poznámka byly pryč.
     *
     * Loader proto zůstává jen tam, kde se opravdu nemá co ukázat: při prvním
     * načtení a při přepnutí na příští směnu (`forceNext`). Obojí jde přes
     * efekt níž; všechna ostatní volání jsou tichá.
     */
    const fetchSmena = useCallback(async (tise = false) => {
        if (!tise) setLoading(true);
        try {
            // forceNext=true (po X v post-completion placeholderu) přeskočí
            // dnešní dokončené smeny → backend vrátí příští naplánovanou.
            const url = forceNext
                ? '/api/me/smena/dnes?force_next=true'
                : '/api/me/smena/dnes';
            const res = await fetch(url, { headers: { Accept: 'application/json' } });
            const data = await res.json();
            setSmena(data.smena);
            setDochazka(data.dochazka);
            setPokladnaSmena(data.pokladna_smena);
            setTym(data.tym || []);
            setTypUvazku(data.user?.typ_uvazku || null);
            setUkoly(data.ukoly || []);
            // Bez směny je otázka bezpředmětná; s budoucí směnou (volno)
            // přijde false. Starší backend pole neposílá — pak se bere true,
            // což je stav před touhle změnou.
            setJeDnes(data.smena ? (data.je_dnes ?? true) : true);
        } finally {
            setLoading(false);
        }
    }, [forceNext]);

    useEffect(() => { fetchSmena(); }, [fetchSmena]);

    // Ven jde jen tichá varianta — volající obnovují data k obrazovce, kterou
    // uživatel zrovna má před sebou. Identita musí být stabilní: visí na ní
    // deps efektů v modulu (Reverb listener, 45s pojistka).
    const refetch = useCallback(() => fetchSmena(true), [fetchSmena]);

    // Zahájí "přípravu" — vytvoří dochazku BEZ clock_in (uživatel teprve
    // vyplňuje úvodní checklisty + vybírá auto/pokladnu). Lazy-create
    // checklist instancí pro fázi zahajeni v backendu.
    const zahajitPripravu = useCallback(async () => {
        if (!smena) return { ok: false };
        const res = await fetch(`/api/me/smena/${smena.id}/zahajit-pripravu`, {
            method: 'POST', headers: jsonHeaders(),
        });
        const data = await res.json().catch(() => null);
        if (res.ok) await fetchSmena(true);
        return { ok: res.ok, error: data?.error };
    }, [smena, fetchSmena]);

    // Smaže přípravu — dochazku bez clock_in + všechny lazy-vytvořené
    // checklist instance. Použití: klik "← Fáze: Zahájení" v starting kartě.
    const zrusitPripravu = useCallback(async () => {
        if (!smena) return { ok: false };
        const res = await fetch(`/api/me/smena/${smena.id}/zrusit-pripravu`, {
            method: 'POST', headers: jsonHeaders(),
        });
        if (res.ok) await fetchSmena(true);
        return { ok: res.ok };
    }, [smena, fetchSmena]);

    const clockIn = useCallback(async (clockManual = null, vozidloId = null, pokladnaId = null, manazerPin = null) => {
        if (!smena) return { ok: false };
        const payload = {};
        if (clockManual) payload.clock_manual = clockManual;
        if (vozidloId) payload.vozidlo_id = vozidloId;
        if (pokladnaId) payload.pokladna_id = pokladnaId;
        if (manazerPin) payload.manazer_pin = manazerPin;
        const body = Object.keys(payload).length ? JSON.stringify(payload) : undefined;
        const res = await fetch(`/api/me/smena/${smena.id}/clock-in`, { method: 'POST', headers: jsonHeaders(), body });
        const data = await res.json().catch(() => null);
        if (res.ok) await fetchSmena(true);
        return {
            ok: res.ok,
            status: res.status,
            error: data?.error,
            requires_manager_pin: data?.requires_manager_pin === true,
            zpozdeni_min: data?.zpozdeni_min,
            planovany_start: data?.planovany_start,
            tolerance_min: data?.tolerance_min,
            retro_jizdy_count: data?.retro_jizdy_count,
            retro_pohyby_count: data?.retro_pohyby_count,
            pin_pozdni_zahajeni: data?.pin_pozdni_zahajeni,
        };
    }, [smena, fetchSmena]);

    const clockOut = useCallback(async (clockManual = null) => {
        if (!smena || !dochazka?.clock_in) return;
        const body = clockManual ? JSON.stringify({ clock_manual: clockManual }) : undefined;
        const res = await fetch(`/api/me/smena/${smena.id}/clock-out`, { method: 'POST', headers: jsonHeaders(), body });
        if (res.ok) await fetchSmena(true);
        return res.ok;
    }, [smena, dochazka, fetchSmena]);

    const vratitAuto = useCallback(async () => {
        if (!smena) return false;
        const res = await fetch(`/api/me/smena/${smena.id}/vratit-auto`, { method: 'POST', headers: jsonHeaders() });
        if (res.ok) await fetchSmena(true);
        return res.ok;
    }, [smena, fetchSmena]);

    const prevzitAuto = useCallback(async (vozidloId) => {
        if (!smena || !vozidloId) return { ok: false };
        const res = await fetch(`/api/me/smena/${smena.id}/prevzit-auto`, {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify({ vozidlo_id: vozidloId }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) await fetchSmena(true);
        return { ok: res.ok, error: data?.error };
    }, [smena, fetchSmena]);

    // Výměna za běhu jedním krokem — volné auto, nebo prohození s kolegou.
    const vymenitAuto = useCallback(async (vozidloId) => {
        if (!smena || !vozidloId) return { ok: false };
        const res = await fetch(`/api/me/smena/${smena.id}/vymenit-auto`, {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify({ vozidlo_id: vozidloId }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) await fetchSmena(true);
        return { ok: res.ok, error: data?.error, prohozeno: data?.prohozeno };
    }, [smena, fetchSmena]);

    // Potvrzení výplaty zálohy PINem. Momentálně bez UI: jediným konzumentem
    // byl `PayoutPanel`, který visel v nedosažitelné větvi `index.jsx` a smazal
    // se s ní. Endpoint i manažerská strana ve Směnách žijí dál, takže se sem
    // metoda nechává k zapojení, až bude kam.
    const payoutConfirm = useCallback(async (pin) => {
        if (!smena) return { ok: false };
        const res = await fetch(`/api/me/smena/${smena.id}/payout-confirm`, {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify({ manager_pin: pin }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) await fetchSmena(true);
        return { ok: res.ok, status: res.status, ...data };
    }, [smena, fetchSmena]);

    // Manažer potvrdí vyúčtování kolegy. Bez PINu — je přihlášený na vlastním
    // telefonu. Refetch je nutný: potvrzením zmizí úkol jemu a odemkne se
    // odhlášení kolegovi.
    // `ukol` = celý úkol z karty. Vyrovnání bez pokladny (jen záloha ze
    // mzdy) má vlastní endpoint — pokladní smena u něj neexistuje.
    const potvrditVyuctovani = useCallback(async (ukol, reseniManka = null, dochazkaEdit = null) => {
        const jeZaloha = ukol?.typ === 'zaloha';
        const url = jeZaloha
            ? `/api/me/zaloha/${ukol.dochazka_id}/potvrdit`
            : `/api/me/vyuctovani/${ukol?.pokladna_smena_id ?? ukol}/potvrdit`;
        const res = await fetch(url, {
            method: 'POST',
            headers: jsonHeaders(),
            // Korekce docházky jede spolu s potvrzením, ne zvlášť: mzda z ní
            // vstupuje do částky, kterou manažer právě předává, takže se obojí
            // musí zapsat naráz, nebo vůbec.
            body: JSON.stringify(jeZaloha
                ? { dochazka_edit: dochazkaEdit }
                : { reseni_manka: reseniManka, dochazka_edit: dochazkaEdit }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(chybaZOdpovedi(res, data));
        await fetchSmena(true);
        return true;
    }, [fetchSmena]);

    const sendMessage = useCallback(async ({ typ, text, prijemce_zamestnanec_id }) => {
        if (!smena) return false;
        const body = { typ, text, ...(typ === 'individual' && { prijemce_zamestnanec_id }) };
        const res = await fetch(`/api/me/smena/${smena.id}/zprava`, {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify(body),
        });
        return res.ok;
    }, [smena]);

    const dokoncitFazi = useCallback(async (fazeId, clockManual = null) => {
        if (!smena) return { ok: false };
        const body = clockManual ? JSON.stringify({ clock_manual: clockManual }) : undefined;
        const res = await fetch(`/api/me/smena/${smena.id}/faze/${fazeId}/dokoncit`, { method: 'POST', headers: jsonHeaders(), body });
        const data = await res.json().catch(() => ({}));
        if (res.ok) await fetchSmena(true);
        return { ok: res.ok, status: res.status, ...data };
    }, [smena, fetchSmena]);

    /** Ukončení omylem → zpět do průběhu směny (server hlídá, že to ještě jde). */
    const vratitDoPrubehu = useCallback(async () => {
        if (!smena) return { ok: false };
        const res = await fetch(`/api/me/smena/${smena.id}/vratit-do-prubehu`, { method: 'POST', headers: jsonHeaders() });
        const data = await res.json().catch(() => ({}));
        if (res.ok) await fetchSmena(true);
        return { ok: res.ok, ...data };
    }, [smena, fetchSmena]);

    // Optimistické uložení odpovědi — okamžitě přepiš položku v lokálním stavu,
    // pak teprve API. Bez full refetch (který by blikl celou obrazovkou). Při
    // chybě se rollbackne zpátky. Přebírá data shape kompatibilní s backendem
    // (hodnota_bool | hodnota_cislo | hodnota_vyber | hodnota_text | poznamka_akce).
    const saveOdpoved = useCallback(async (checklistId, polozkaId, data) => {
        let prevState = null;
        setSmena(s => {
            prevState = s;
            if (!s) return s;
            return {
                ...s,
                faze: (s.faze || []).map(f => ({
                    ...f,
                    checklisty: (f.checklisty || []).map(ch => {
                        if (ch.id !== checklistId) return ch;
                        let done = 0;
                        const items = (ch.items || []).map(it => {
                            if (it.id !== polozkaId) {
                                if (it.splneno) done++;
                                return it;
                            }
                            const merged = { ...(it.odpoved || {}), ...data };
                            // Splneno počítáme z mergeovaného stavu — samotné uložení
                            // poznámky (poznamka_akce) nesmí shodit splnění položky.
                            const splneno = merged.hodnota_bool === true
                                || (merged.hodnota_cislo !== null && merged.hodnota_cislo !== undefined && merged.hodnota_cislo !== '')
                                || (!!merged.hodnota_vyber)
                                || (!!merged.hodnota_text);
                            if (splneno) done++;
                            return { ...it, odpoved: merged, splneno };
                        });
                        return { ...ch, items, done_count: done };
                    }),
                })),
            };
        });

        const res = await fetch(`/api/checklisty/${checklistId}/odpovedi`, {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify({ polozka_id: polozkaId, ...data }),
        });

        if (!res.ok) {
            // Rollback — obnovíme původní stav, aby UI odráželo realitu v DB.
            setSmena(prevState);
            return false;
        }
        // Server rozhoduje o odchylce (limit, věrohodnost odečtu) — jeho
        // `mimo_limit` a výsledek měření (návrh opravy překlepu) přepíšou
        // optimistický stav. Dřív kokpit odchylku neviděl, dokud se nenačetl znovu.
        const server = await res.json().catch(() => null);
        if (server) {
            setSmena(s => s && ({
                ...s,
                faze: (s.faze || []).map(f => ({
                    ...f,
                    checklisty: (f.checklisty || []).map(ch => ch.id !== checklistId ? ch : {
                        ...ch,
                        items: (ch.items || []).map(it => it.id !== polozkaId ? it : {
                            ...it,
                            odpoved: { ...(it.odpoved || {}), mimo_limit: !!server.mimo_limit, mereni: server.mereni ?? it.odpoved?.mereni ?? null },
                        }),
                    }),
                })),
            }));
        }
        return true;
    }, []);

    // Zpětně kompatibilní boolean toggle — jen wrapper nad saveOdpoved.
    const togglePolozka = useCallback(
        (checklistId, polozkaId, splneno) => saveOdpoved(checklistId, polozkaId, { hodnota_bool: !!splneno }),
        [saveOdpoved]
    );

    return { smena, dochazka, pokladnaSmena, tym, typUvazku, jeDnes, ukoly, loading, refetch, potvrditVyuctovani, zahajitPripravu, zrusitPripravu, clockIn, clockOut, vratitAuto, prevzitAuto, vymenitAuto, payoutConfirm, sendMessage, togglePolozka, saveOdpoved, dokoncitFazi, vratitDoPrubehu };
}
